import { Pagination } from "@mui/material";
import {
  AvatarIcon,
  CalendarIcon,
  PersonIcon,
  SewingPinFilledIcon,
} from "@radix-ui/react-icons";
import { Loader2, Package, Search, Trash2, X } from "lucide-react";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { find, store } from "../../services/service";
import { useStoreApp } from "../../store/storeApp";
import {
  ATTRIBUER_MAINTENANCE_EQUIPE,
  LISTE_ATTRIBUE_MAINTENANCE_PRESTATAIRE,
  LISTE_EQUIPE_PRESTATAIRE,
  PRESTATAIRE,
  RETIRER_MAINTENANCE_PRESTATAIRE,
} from "../../Utils/constant";
import { checkTagValidity } from "../../Utils/Utils";
import { getPrioriteBadge } from "./GestionMaintenance";
import { StatusMaintenanceModal } from "./StatusMaintenanceModal";

export const PrestataireAttributionMaintenance = () => {
  const prestataire = useStoreApp();
  const [message, setMessage] = useState("");
  const [item, setItem] = useState(null);

  const [prestataireId, setPrestataireId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [selectedPrestataireDossiers, setSelectedPrestataireDossiers] =
    useState([]);
  const [loadingDossiers, setLoadingDossiers] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [dossierPage, setDossierPage] = useState(0);
  const [dossierTotalPages, setDossierTotalPages] = useState(1);
  const [msg, setMsg] = useState("");
  const [assignModalItem, setAssignModalItem] = useState(null);
  const [isAssigning, setIsAssigning] = useState(false);
  const [selectedTeamPrestataire, setSelectedTeamPrestataire] = useState("");
  const [equipe, setEquipe] = useState([]);

  // Nouveaux états pour la sélection et le retrait
  const [selectedDossiers, setSelectedDossiers] = useState([]);
  const [isRemoving, setIsRemoving] = useState(false);
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [selected, setSelected] = useState(null);
  const [newStatus, setNewStatus] = useState("");
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  useEffect(() => {
    prestataire.getAll(PRESTATAIRE);
  }, []);

  // Gestion du changement de commune
  const handlePrestataireChange = (value) => {
    setPrestataireId(value);
    setItem(null);
  };
  const getListEquipe = async (prestataireId) => {
    try {
      const res = await find(`${LISTE_EQUIPE_PRESTATAIRE}/${prestataireId}`);
      setEquipe(res?.data?.donnee || []);
    } catch (err) {
      console.error(err);
      toast.error("Erreur de chargement des équipes du prestataire");
      setEquipe([]);
    }
  };

  // Charger dossiers pour le prestataire sélectionné (avec pagination)
  const fetchDossiers = async (page = 0, param) => {
    setLoadingDossiers(true);
    try {
      const res = await find(
        `${LISTE_ATTRIBUE_MAINTENANCE_PRESTATAIRE}?page=${page ?? 0}&param=${param ?? ""}&prestataireId=${prestataireId}&size=10`,
      );

      setSelectedPrestataireDossiers(res?.data?.donnee?.data || []);
      setDossierTotalPages(res?.data?.donnee?.totalPages ?? 1);
      setDossierPage(page);
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.donnee || "Erreur chargement dossiers");
      setSelectedPrestataireDossiers([]);
      setDossierTotalPages(1);
      setDossierPage(0);
    } finally {
      setLoadingDossiers(false);
    }
  };

  const handleSearch = async (value) => {
    setSearchTerm(value);
    setIsSearching(true);
    setCurrentPage(0);
    try {
      fetchDossiers(0, value);
    } finally {
      setIsSearching(false);
    }
  };

  useEffect(() => {
    fetchDossiers(0, searchTerm);
  }, [searchTerm, prestataireId]);

  const handleAssignTeamPrestataire = async () => {
    if (!assignModalItem || !selectedTeamPrestataire) return;

    setIsAssigning(true);
    setMsg("");

    try {
      const res = await store(
        `${ATTRIBUER_MAINTENANCE_EQUIPE}${assignModalItem?.id}/equipe/${selectedTeamPrestataire}`,
        "application/json",
        {},
      );

      if (res.status === 200) {
        fetchDossiers(dossierPage);
        document.getElementById("assign-modal-equipe").close();
        setAssignModalItem(null);
        setSelectedTeamPrestataire("");
        toast.success("Prestataire affecté avec succès");
      }
    } catch (err) {
      const errorMsg =
        err?.response?.data?.donnee || "Erreur lors de l'attribution";
      setMsg(errorMsg);
      toast.error(errorMsg);
    } finally {
      setIsAssigning(false);
    }
  };

  // Gestion de la sélection des dossiers
  const handleSelectDossier = (dossierId) => {
    setSelectedDossiers((prev) => {
      if (prev.includes(dossierId)) {
        return prev.filter((id) => id !== dossierId);
      } else {
        return [...prev, dossierId];
      }
    });
  };

  const handleStatusUpdate = () => {
    // Refresh the dossiers list when status is updated
    fetchDossiers(dossierPage);
    toast.success("Statut modifié avec succès");
  };

  // Sélectionner/désélectionner tous les dossiers
  const handleSelectAll = () => {
    if (selectedDossiers.length === selectedPrestataireDossiers.length) {
      setSelectedDossiers([]);
    } else {
      setSelectedDossiers(selectedPrestataireDossiers.map((d) => d?.id));
    }
  };

  // Fonction pour retirer les dossiers sélectionnés
  const handleRemoveDossiers = async () => {
    if (selectedDossiers.length === 0) return;
    setIsRemoving(true);
    try {
      store(RETIRER_MAINTENANCE_PRESTATAIRE, "application/json", {
        ids: selectedDossiers,
      }).then(() => {
        toast.success(
          `${selectedDossiers.length} dossier(s) retiré(s) avec succès`,
        );
        setSelectedDossiers([]);
        setShowRemoveModal(false);
        fetchDossiers(dossierPage);
      });
    } catch (err) {
      const errorMsg =
        err?.response?.data?.donnee || "Erreur lors du retrait des dossiers";
      toast.error(errorMsg);
    } finally {
      setIsRemoving(false);
    }
  };

  return (
    <div className="mt-4">
      <div className="my-4 flex flex-col md:flex-row items-stretch md:items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <input
            value={searchTerm}
            onChange={(e) => handleSearch(e.target.value)}
            type="text"
            placeholder="Rechercher un element.."
            className="input input-bordered px-3 pl-10 pr-10 w-full h-11 text-sm text-gray-900 placeholder:text-gray-400 focus:ring-2 focus:ring-amber-400 focus:border-amber-400"
            disabled={prestataire?.loading}
          />
          <Search className="w-4 h-4 absolute left-3 top-3.5 text-gray-400" />
          {isSearching && (
            <Loader2 className="w-4 h-4 animate-spin absolute right-3 top-3.5 text-amber-500" />
          )}
          {searchTerm && !isSearching && (
            <button
              onClick={() => handleSearch("")}
              className="absolute right-3 top-3.5 text-gray-400 hover:text-gray-600"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        <select
          className="select select-bordered h-11 text-sm min-w-[200px] focus:ring-2 focus:ring-amber-400 focus:border-amber-400"
          value={prestataireId}
          onChange={(e) => handlePrestataireChange(e.target.value)}
          disabled={prestataire?.loading}
        >
          <option value="">
            {prestataire?.loading ? "Chargement..." : "Tous les prestataires"}
          </option>
          {prestataire?.liste?.map((c) => (
            <option key={c.id} value={c.id}>
              {c.designation}
            </option>
          ))}
        </select>
      </div>

      <div className="">
        {/* Liste des prestataires */}

        <dialog id="assign-modal-equipe" className="modal">
          <div className="modal-box w-96">
            <h3 className="font-bold text-lg">Affecter à une equipe</h3>
            <p className="text-xs text-gray-500 mb-3 ">
              Sélectionnez une equipe à affecter pour ce rendez-vous.
            </p>
            {msg && <p className="text-red-500 text-sm mb-2">{msg}</p>}
            <div className="mb-3">
              {equipe?.length === 0 ? (
                <p className="text-sm text-gray-500 my-8">
                  Aucune equipe disponible pour ce prestataire.
                </p>
              ) : (
                <select
                  value={selectedTeamPrestataire}
                  onChange={(e) => setSelectedTeamPrestataire(e.target.value)}
                  className="select select-bordered w-full"
                  disabled={isAssigning}
                >
                  <option value="">-- Choisir une equipe --</option>
                  {equipe?.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.designation}
                    </option>
                  ))}
                </select>
              )}
            </div>
            <div className="modal-action">
              <button
                onClick={handleAssignTeamPrestataire}
                disabled={isAssigning || !selectedTeamPrestataire}
                className={`btn btn-sm ${
                  isAssigning || !selectedTeamPrestataire
                    ? "btn-disabled opacity-50"
                    : "bg-main text-black border-main"
                }`}
              >
                {isAssigning ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Affectation...
                  </>
                ) : (
                  "Affecter"
                )}
              </button>
              <button
                onClick={() => {
                  document.getElementById("assign-modal-equipe").close();
                  setAssignModalItem(null);
                  setMsg("");
                }}
                disabled={isAssigning}
                className="btn btn-sm"
              >
                Annuler
              </button>
            </div>
          </div>
        </dialog>

        {/* Modal de confirmation de retrait */}
        <dialog
          id="remove-modal"
          className={`modal ${showRemoveModal ? "modal-open" : ""}`}
        >
          <div className="modal-box">
            <h3 className="font-bold text-lg text-gray-900">
              Confirmer le retrait
            </h3>
            <p className="text-sm text-gray-600 py-4">
              Êtes-vous sûr de vouloir retirer {selectedDossiers.length}{" "}
              dossier(s) de ce prestataire ?
            </p>
            <div className="modal-action">
              <button
                onClick={handleRemoveDossiers}
                disabled={isRemoving}
                className="btn btn-sm bg-red-500 text-white hover:bg-red-600 border-red-500"
              >
                {isRemoving ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Retrait...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4" />
                    Confirmer
                  </>
                )}
              </button>
              <button
                onClick={() => setShowRemoveModal(false)}
                disabled={isRemoving}
                className="btn btn-sm"
              >
                Annuler
              </button>
            </div>
          </div>
        </dialog>

        {/* Dossiers du prestataire sélectionné */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-lg shadow-sm p-4">
            <>
              {/* Barre d'actions pour sélection */}
              {selectedPrestataireDossiers.length > 0 &&
              prestataireId &&
              checkTagValidity("RETIRER_PRESTATAIRE") ? (
                <div className="mb-4 p-3 bg-gray-50 rounded-lg border border-gray-200 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        className="checkbox checkbox-sm checkbox-warning"
                        checked={
                          selectedDossiers.length ===
                          selectedPrestataireDossiers.length
                        }
                        onChange={handleSelectAll}
                      />
                      <span className="text-sm font-medium text-gray-700">
                        Tout sélectionner
                      </span>
                    </label>
                    {selectedDossiers.length > 0 && (
                      <span className="text-xs text-gray-500">
                        ({selectedDossiers.length} sélectionné
                        {selectedDossiers.length > 1 ? "s" : ""})
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {selectedDossiers.length > 0 && (
                      <button
                        onClick={() => setShowRemoveModal(true)}
                        className="btn btn-sm bg-red-500 text-white hover:bg-red-600 border-red-500"
                      >
                        <Trash2 className="w-4 h-4" />
                        Retirer ({selectedDossiers.length})
                      </button>
                    )}
                  </div>
                </div>
              ) : null}

              {/* Contenu */}
              {loadingDossiers ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-amber-500 mb-3" />
                  <p className="text-sm text-gray-600">
                    Chargement des dossiers...
                  </p>
                </div>
              ) : selectedPrestataireDossiers.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <div className="bg-gray-100 rounded-full p-4 mb-3">
                    <Package className="w-8 h-8 text-gray-400" />
                  </div>
                  <p className="text-sm text-gray-600 font-medium mb-1">
                    Aucun dossier attribué
                  </p>
                  <p className="text-xs text-gray-400 text-center px-4">
                    Ce prestataire n'a pas encore de dossiers
                  </p>
                </div>
              ) : (
                <div className="grid md:grid-cols-3 gap-4 ">
                  {selectedPrestataireDossiers.map((d) => (
                    <div
                      key={d.id}
                      className={`border rounded-lg p-4 hover:shadow-md transition-all ${
                        selectedDossiers.includes(d.abonnement?.id)
                          ? "border-amber-200 bg-amber-50"
                          : "border-gray-200 bg-white"
                      }`}
                    >
                      {/* Checkbox de sélection */}
                      <div className="flex items-start justify-between mb-2">
                        {prestataireId &&
                        checkTagValidity("RETIRER_PRESTATAIRE") ? (
                          <input
                            type="checkbox"
                            className="checkbox checkbox-sm checkbox-warning mt-1"
                            checked={selectedDossiers.includes(d?.id)}
                            onChange={() => handleSelectDossier(d?.id)}
                          />
                        ) : null}
                      </div>

                      {/* Contenu */}
                      <div className="space-y-2">
                        <div className="rounded-t-xl flex items-center gap-3 border-b border-dashed border-amber-400 px-2 py-3">
                          <p className="text-[12px] font-semibold">
                            N° {d?.id}
                          </p>
                          <span className={getPrioriteBadge(d.priorite)}>
                            {d.priorite}
                          </span>
                        </div>
                        <div className="px-3 py-2">
                          <div className="items-center gap-2 space-y-3">
                            <div className="flex gap-1 my-3">
                              <PersonIcon className="bg-gray-100 rounded-full w-8 h-8 p-2" />
                              <div>
                                <p className="text-[13px] font-medium">
                                  {d?.nomPrenomsClient}
                                </p>
                                <p className="text-xs flex font-semibold gap-2">
                                  <span>{d?.contactClient}</span>
                                </p>
                              </div>
                            </div>
                            <div className="flex gap-2">
                              <div className=" w-fit px-1 py-[2px]">
                                {d?.dateCreation && (
                                  <p className="text-xs flex gap-1 items-center font-medium">
                                    <CalendarIcon />
                                    {new Date(
                                      d?.dateCreation,
                                    ).toLocaleDateString("fr-FR", {
                                      day: "2-digit",
                                      month: "2-digit",
                                      year: "2-digit",
                                      hour: "2-digit",
                                      minute: "2-digit",
                                    })}
                                  </p>
                                )}
                              </div>
                            </div>

                            <div className="gap-1">
                              <div>
                                <p className="text-[13px] font-medium">
                                  Numero FTTH : {d?.numeroFtth}
                                </p>
                              </div>
                            </div>

                            <h1 className="text-xs flex gap-1 items-center my-2">
                              <AvatarIcon />
                              Rapporteur : {d?.rapporteur?.agent?.nomPrenoms}
                            </h1>
                            {d.description && (
                              <div className="dropdown flex items-center">
                                <p className="text-xs">{d?.description}</p>
                              </div>
                            )}
                          </div>

                          {d?.commune && (
                            <h1 className="my-2 text-[13px] font-medium flex items-center">
                              <SewingPinFilledIcon />
                              {d?.commune?.designation}
                            </h1>
                          )}

                          {d?.prestataire ? (
                            <h1 className="text-xs flex gap-1 p-1 w-fit rounded items-center my-2">
                              Prestataire : {d?.prestataire?.designation}
                            </h1>
                          ) : null}

                          {d?.raison && (
                            <div className="my-2 p-2 bg-red-50 border border-red-200 rounded-lg">
                              <p className="text-xs text-red-700 font-semibold">
                                Raison:
                              </p>
                              <p className="text-xs text-red-600 mt-1">
                                {d?.raison}
                              </p>
                            </div>
                          )}

                          <div className="flex items-center gap-1">
                            <p className="bg-blue-50 text-blue-900 py-1 px-1 rounded-lg font-medium text-xs truncate">
                              {d?.statut?.toLowerCase().replace(/_/g, " ")}
                            </p>
                          </div>
                        </div>
                        {d?.equipe && (
                          <div className="pt-2">
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-50 text-green-700">
                              Équipe: {d?.equipe?.designation}
                            </span>
                          </div>
                        )}
                        <div className="flex items-center gap-1">
                          <StatusMaintenanceModal
                            item={d}
                            onStatusUpdate={handleStatusUpdate}
                          />
                        </div>

                        <dialog id="edit" className="modal ">
                          <div className="modal-box w-10/12 max-w-2xl">
                            <div className="modal-action">
                              <h1 className="mr-auto text-2xl font-bold font-mtn">
                                Modification de l'abonnement
                              </h1>
                              <form method="dialog">
                                <button
                                  id="fermer"
                                  className="w-8 h-8 flex items-center justify-center rounded-lg bg-red-100 hover:bg-red-200 text-red-600 hover:text-red-600 font-semibold"
                                >
                                  ✕
                                </button>
                              </form>
                            </div>
                            <p className="text-sm text-red-500">{message}</p>

                            <form className="my-4"></form>
                          </div>
                        </dialog>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <Pagination
                className="mt-4 flex justify-center"
                page={dossierPage + 1}
                onChange={(event, newValue) => fetchDossiers(newValue - 1)}
                count={dossierTotalPages}
                size="small"
                variant="outlined"
                color="primary"
                shape="rounded"
                disabled={loadingDossiers}
              />
            </>
          </div>
        </div>
      </div>
    </div>
  );
};
